/**
 * Notifications in the browser (backend routes/notifications.ts, docs/NOTIFICATIONS.md).
 *
 * One small shared store for the unread counts, so the header bell, a room
 * bar's bell, the side panel and Settings › Notifications all agree: marking
 * something read in one place clears the red count everywhere at once.
 * The counts poll every 30 s while the tab is visible, and at once when it
 * becomes visible again.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { apiJson } from './api';

export type NotifyKind =
  | 'upvote' | 'award' | 'reply' | 'mention' | 'mod' | 'rank' | 'kharma' | 'welcome'
  | 'site_user' | 'site_camp' | 'site_report' | 'site_held' | 'site_feedback' | 'site_award' | 'site_kharma';
export type NotifyTab = 'all' | 'unread' | 'site';

/** What a row's picture shows (backend routes/notifications.ts `picKey`). */
export type NoticePic =
  | { type: 'camp'; slug: string; art: { emblem?: string; scene?: string; palette?: string } | null }
  | { type: 'game'; id: string }
  | { type: 'app'; name: string; src: string | null }
  | { type: 'award'; award: string }
  | { type: 'rank'; level: number; down: boolean }
  | { type: 'coin' } | { type: 'brand' } | { type: 'person' };

export interface Notice {
  id: string; kind: NotifyKind; title: string; body: string; href: string; count: number; amount: number;
  actor: { name: string; avatar: string | null } | null; pic?: NoticePic | null; read: boolean; site: boolean; at: string;
}

export const fetchNotices = (tab: NotifyTab, cursor?: string | null) =>
  apiJson<{ notifications: Notice[]; next: string | null; admin: boolean }>(`/api/notifications?${new URLSearchParams({ tab, ...(cursor ? { cursor } : {}) })}`);
export const markRead = async (b: { ids?: string[]; tab?: NotifyTab }) => {
  await apiJson<{ ok: true }>('/api/notifications/read', { method: 'POST', body: JSON.stringify(b) });
  void refreshCounts();
};
export const fetchPrefs = () => apiJson<{ off: string[]; admin: boolean }>('/api/notifications/prefs');
export const savePrefs = (off: string[]) => apiJson<{ off: string[] }>('/api/notifications/prefs', { method: 'PUT', body: JSON.stringify({ off }) });

/** The switches in Settings › Notifications. `site` is the admins' Site list. */
export const PREF_KINDS: { id: string; label: string; hint: string; admin?: boolean }[] = [
  { id: 'upvote', label: 'Upvotes', hint: 'Your posts and comments getting upvotes, and the KHARMA they pay' },
  { id: 'award', label: 'Awards', hint: 'Someone giving your post or comment an award' },
  { id: 'reply', label: 'Replies', hint: 'Comments on your posts and replies to your comments' },
  { id: 'mention', label: 'Mentions', hint: 'Someone writing u/your name' },
  { id: 'mod', label: 'Moderation', hint: 'Removals, approvals, bans, join requests, becoming a moderator' },
  { id: 'rank', label: 'Rank', hint: 'Reaching a new rank' },
  { id: 'kharma', label: 'KHARMA', hint: 'Changes to your wallet by the TrueMarch team, season prizes' },
  { id: 'site', label: 'Site events', hint: 'New accounts, Camps, reports, held posts, feedback, awards', admin: true },
];

// ── The shared unread counts ─────────────────────────────────────────────────

type Counts = { mine: number; site: number; admin: boolean; loaded: boolean };
let counts: Counts = { mine: 0, site: 0, admin: false, loaded: false };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
let inflight: Promise<void> | null = null;

export function refreshCounts(): Promise<void> {
  inflight ??= apiJson<{ mine: number; site: number; admin: boolean }>('/api/notifications/count')
    .then((c) => { counts = { ...c, loaded: true }; emit(); })
    .catch(() => undefined)
    .finally(() => { inflight = null; });
  return inflight;
}

let users = 0;
let timer: ReturnType<typeof setInterval> | null = null;
const onVisible = () => { if (document.visibilityState === 'visible') void refreshCounts(); };

/** The unread counts, kept fresh while any component using them is on the page. */
export function useNotifyCounts(enabled: boolean): Counts {
  const c = useSyncExternalStore(
    (l) => { listeners.add(l); return () => listeners.delete(l); },
    () => counts,
    () => counts,
  );
  useEffect(() => {
    if (!enabled) return;
    users++;
    if (users === 1) {
      void refreshCounts();
      timer = setInterval(() => { if (document.visibilityState === 'visible') void refreshCounts(); }, 30_000);
      document.addEventListener('visibilitychange', onVisible);
    }
    return () => {
      users--;
      if (users === 0) {
        if (timer) clearInterval(timer);
        timer = null;
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [enabled]);
  return c;
}

/** "3m", "2h", "5d" — compact, like Reddit's inbox. */
export function ago(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
